import { inject } from '@angular/core';
import { UserFullInfoService } from '@core/services/user-full-info.service';
import { EntityPhotosComponent } from '@shared/components/entity-photos/entity-photos.component';
import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Subject, forkJoin, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap, takeUntil, tap } from 'rxjs/operators';

import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatTabsModule } from '@angular/material/tabs';
import { BreadcrumbService } from '@core/services/breadcrumb.service';
import { TableColumn } from '@shared/components/data-table/data-table.component';
import { modalDialogConfig } from '@shared/components/modal-shell/modal-dialog.config';
import { TableSurfaceComponent } from '@shared/components/table-surface/table-surface.component';
import { ToolbarSortSelection } from '@shared/components/table-toolbar/table-toolbar.component';
import { formatUtcDateTimeForUser } from '@shared/utils/utc-date.util';
import { PersonService } from '@features/people/services/person.service';

import {
  EligiblePerson,
  UpsertUserRoleAssignmentDto,
  UserRole,
  UserRoleAssignmentDto,
  UserService,
  CreateUserRequest,
  UserActivitySummary,
} from '../../services/user.service';
import { DeleteConfirmDialogComponent } from '../shared/delete-confirm-dialog/delete-confirm-dialog.component';
import { UserEmailEditDialogComponent } from './user-email-edit-dialog.component';
import { UserRoleAssignmentDialogComponent, UserRoleAssignmentDialogResult } from './user-role-assignment-dialog.component';

@Component({
  selector: 'app-user-editor',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [EntityPhotosComponent,
    CommonModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatDialogModule,
    MatTabsModule,
    TableSurfaceComponent
  ],
  templateUrl: './user-editor.component.html',
  styleUrls: ['./user-editor.component.scss']
})
export class UserEditorComponent implements OnInit, OnDestroy {
  private readonly photoUser = inject(UserFullInfoService);
  get photoPersonId() { return this.photoUser.getUserInfoSnapshot()?.personId; }
  editorForm!: FormGroup;
  personSearchControl = new FormControl<string | EligiblePerson>('');
  eligiblePeople: EligiblePerson[] = [];
  selectedPerson: EligiblePerson | null = null;
  isEditMode = false;
  isSaving = false;
  isLoading = false;
  isDeleting = false;
  isSearchingPeople = false;
  loadError: string | null = null;
  userFullName = '';
  userEmail = '';
  username = '';
  userDocument = '';
  membershipUnitName = '';
  hasLocalUser = false;
  roleAssignments: UserRoleAssignmentDto[] = [];
  roleAssignmentsTableData: UserRoleAssignmentDto[] = [];
  roleAssignmentSearchTerm = '';
  roleAssignmentSortState: ToolbarSortSelection = { sortBy: 'role', direction: 'asc' };
  activitySummary: UserActivitySummary | null = null;
  selectedDetailTabIndex = 0;
  isSelfProfile = false;
  readonly roleAssignmentColumns: TableColumn[] = [
    { key: 'role', header: 'Rol', width: '20%', sortable: true, hideable: true, visible: true, resizable: true },
    { key: 'unit', header: 'Unidad', sortable: true, hideable: true, visible: true, resizable: true },
    { key: 'actions', header: 'Acciones', width: '120px' },
  ];
  readonly selfRoleAssignmentColumns: TableColumn[] = [
    { key: 'role', header: 'Rol', width: '20%', sortable: true, hideable: true, visible: true, resizable: true },
    { key: 'unit', header: 'Unidad', sortable: true, hideable: true, visible: true, resizable: true },
  ];
  readonly roleOptions = [
    { value: 'UnitManager', label: 'Responsable de unidad', description: 'Puede gestionar la unidad asignada y sus descendientes.' },
    { value: 'Servant', label: 'Siervo', description: 'Puede consultar miembros y operar servicios divinos en la unidad asignada y sus descendientes.' },
    { value: 'MinisterialManager', label: 'Gestión ministerial regional', description: 'Puede gestionar actividad ministerial en la unidad asignada y sus descendientes.' },
  ];

  private destroy$ = new Subject<void>();
  private userId: string | null = null;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private userService: UserService,
    private personService: PersonService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    private breadcrumbService: BreadcrumbService
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.isSelfProfile = this.activatedRoute.snapshot.data['selfProfile'] === true;
    this.userId = this.activatedRoute.snapshot.paramMap.get('id');
    this.isEditMode = this.isSelfProfile || !!this.userId;

    if (this.isSelfProfile) {
      this.loadSelfProfileForDetail();
    } else if (this.isEditMode) {
      this.loadUserForDetail(this.userId!);
    } else {
      this.setupPersonSearch();
      this.loadPreselectedPerson();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  displayPerson(person: EligiblePerson | string | null): string {
    if (!person || typeof person === 'string') {
      return person ?? '';
    }

    return `${person.lastName ?? ''}, ${person.firstName ?? ''}`.trim();
  }

  getRoleLabel(role: string | null | undefined): string {
    return this.roleOptions.find(option => option.value === role)?.label ?? 'Rol';
  }

  onPersonSelected(person: EligiblePerson): void {
    this.selectedPerson = person;
    this.populatePersonInfo(person);
    this.editorForm.patchValue({
      email: person.email ?? '',
      username: this.suggestUsername(person)
    });
    this.cdr.markForCheck();
  }

  onSave(): void {
    if (this.editorForm.invalid || this.isEditMode || this.isSaving) {
      this.editorForm.markAllAsTouched();
      return;
    }

    if (!this.isEditMode && !this.selectedPerson?.id) {
      this.loadError = 'Seleccione una persona para crear el usuario.';
      this.cdr.markForCheck();
      return;
    }

    this.isSaving = true;
    this.loadError = null;
    this.cdr.markForCheck();

    this.userService.createUser(this.buildCreateRequest())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.router.navigate(['/users']),
        error: (error) => {
          this.loadError = error?.error?.detail || error?.error?.message || 'No se pudo guardar el usuario.';
          this.isSaving = false;
          this.cdr.markForCheck();
        }
      });
  }

  onCancel(): void {
    this.router.navigate(this.isSelfProfile ? ['/'] : ['/users']);
  }

  goBack(): void {
    this.router.navigate(this.isSelfProfile ? ['/'] : ['/users']);
  }

  get currentAccessSummary(): string {
    if (this.roleAssignments.length === 0) {
      return 'Miembro';
    }

    const unitManagers = this.roleAssignments
      .filter(assignment => assignment.role === 'UnitManager')
      .map(assignment => assignment.organizationUnitName || 'Unidad asignada');
    const servants = this.roleAssignments
      .filter(assignment => assignment.role === 'Servant')
      .map(assignment => assignment.organizationUnitName || 'Unidad asignada');
    const ministerialManagers = this.roleAssignments
      .filter(assignment => assignment.role === 'MinisterialManager')
      .map(assignment => assignment.organizationUnitName || 'Unidad asignada');
    const segments: string[] = [];

    if (unitManagers.length > 0) {
      segments.push(`Responsable de unidad: ${unitManagers.join(', ')}`);
    }

    if (servants.length > 0) {
      segments.push(`Siervo: ${servants.join(', ')}`);
    }

    if (ministerialManagers.length > 0) {
      segments.push(`Gestión ministerial regional: ${ministerialManagers.join(', ')}`);
    }

    return segments.length > 0 ? segments.join(' · ') : 'Miembro';
  }

  get accessSummaryChips(): string[] {
    if (this.roleAssignments.length === 0) {
      return ['Miembro'];
    }

    const unitManagers = this.roleAssignments.filter(assignment => assignment.role === 'UnitManager').length;
    const servants = this.roleAssignments.filter(assignment => assignment.role === 'Servant').length;
    const ministerialManagers = this.roleAssignments.filter(assignment => assignment.role === 'MinisterialManager').length;
    const chips: string[] = [];

    if (unitManagers > 0) {
      chips.push(`${unitManagers} Responsable${unitManagers === 1 ? '' : 's'} de unidad`);
    }

    if (servants > 0) {
      chips.push(`${servants} Siervo${servants === 1 ? '' : 's'}`);
    }

    if (ministerialManagers > 0) {
      chips.push(`${ministerialManagers} Gestión ministerial regional`);
    }

    return chips.length > 0 ? chips : ['Miembro'];
  }

  get explicitAccessItems(): UserRoleAssignmentDto[] {
    return this.roleAssignments;
  }

  get displayedRoleAssignments(): UserRoleAssignmentDto[] {
    const search = this.roleAssignmentSearchTerm.trim().toLocaleLowerCase('es');
    const filtered = search
      ? this.roleAssignmentsTableData.filter(assignment =>
          this.getRoleLabel(assignment.role).toLocaleLowerCase('es').includes(search) ||
          (assignment.organizationUnitName ?? '').toLocaleLowerCase('es').includes(search)
        )
      : this.roleAssignmentsTableData;

    return [...filtered].sort((a, b) => {
      const valueA = this.getRoleAssignmentSortValue(a, this.roleAssignmentSortState.sortBy);
      const valueB = this.getRoleAssignmentSortValue(b, this.roleAssignmentSortState.sortBy);
      const result = valueA.localeCompare(valueB, 'es', { sensitivity: 'base' });

      return this.roleAssignmentSortState.direction === 'asc' ? result : -result;
    });
  }

  openEmailDialog(): void {
    const dialogRef = this.dialog.open(UserEmailEditDialogComponent, modalDialogConfig({
      data: { email: this.userEmail },
      size: 'md',
      layout: 'form',
    }));

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(email => {
        if (!email || email === this.userEmail || (!this.isSelfProfile && !this.userId)) {
          return;
        }

        this.isSaving = true;
        const request$ = this.isSelfProfile
          ? this.userService.updateCurrentUserEmail(email)
          : this.userService.updateUserEmail(this.userId!, email);

        request$
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => this.isSelfProfile ? this.loadSelfProfileForDetail() : this.loadUserForDetail(this.userId!),
            error: (error) => {
              this.loadError = error?.error?.detail || error?.error?.message || 'No se pudo guardar el email.';
              this.isSaving = false;
              this.cdr.markForCheck();
            }
          });
      });
  }

  openAccessDialog(): void {
    if (this.isSelfProfile) {
      return;
    }

    this.selectedDetailTabIndex = 1;
    this.cdr.markForCheck();
  }

  openActivityPage(): void {
    if (!this.userId) {
      return;
    }

    this.router.navigate(['/users', this.userId, 'activity']);
  }

  formatDateTime(value?: string | null): string {
    return formatUtcDateTimeForUser(value);
  }

  getActionLabel(actionType?: string | null): string {
    switch ((actionType ?? '').toUpperCase()) {
      case 'LOGIN':
        return 'Inicio de sesion';
      case 'CREATE':
        return 'Creacion';
      case 'UPDATE':
        return 'Edicion';
      case 'DELETE':
        return 'Eliminacion';
      case 'STATUS_CHANGE':
        return 'Cambio de estado';
      case 'ROLE_ASSIGNMENT_UPDATE':
        return 'Roles actualizados';
      case 'TRANSFER_RESOLVE':
        return 'Transferencia resuelta';
      default:
        return actionType || '-';
    }
  }

  saveAccess(): void {
    if (!this.userId || this.isSaving || this.isSelfProfile) {
      return;
    }

    this.isSaving = true;
    this.loadError = null;
    this.cdr.markForCheck();

    this.userService.updateRoleAssignments(this.userId, this.buildRoleAssignmentsRequest(this.roleAssignments))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.loadUserForDetail(this.userId!),
        error: (error) => {
          this.loadError = error?.error?.detail || error?.error?.message || 'No se pudo guardar el acceso.';
          this.isSaving = false;
          this.cdr.markForCheck();
        }
      });
  }

  openRoleAssignmentDialog(assignment?: UserRoleAssignmentDto): void {
    if (this.isSelfProfile || this.isSaving || !this.hasLocalUser) {
      return;
    }

    const dialogRef = this.dialog.open(UserRoleAssignmentDialogComponent, modalDialogConfig({
      data: { assignment },
      size: 'lg',
      layout: 'form',
    }));

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: UserRoleAssignmentDialogResult | null | undefined) => {
        if (!result) {
          return;
        }

        const nextAssignments = assignment
          ? this.roleAssignments.map(item => item === assignment ? this.dialogResultToAssignment(result, assignment.id) : item)
          : [...this.roleAssignments, this.dialogResultToAssignment(result)];

        const validationError = this.validateAssignments(nextAssignments);
        if (validationError) {
          this.loadError = validationError;
          this.cdr.markForCheck();
          return;
        }

        this.persistRoleAssignments(nextAssignments);
      });
  }

  removeRoleAssignment(assignment: UserRoleAssignmentDto): void {
    if (this.isSelfProfile) {
      return;
    }

    const nextAssignments = this.roleAssignments.filter(item => item !== assignment);
    this.persistRoleAssignments(nextAssignments);
  }

  onRoleAssignmentSearch(term: string): void {
    this.roleAssignmentSearchTerm = term;
    this.cdr.markForCheck();
  }

  onRoleAssignmentSort(event: ToolbarSortSelection): void {
    this.roleAssignmentSortState = event;
    this.cdr.markForCheck();
  }

  deleteUnlinkedUser(): void {
    if (!this.userId || this.hasLocalUser || this.isDeleting || this.isSelfProfile) {
      return;
    }

    const dialogRef = this.dialog.open(DeleteConfirmDialogComponent, modalDialogConfig({
      data: {
        title: 'Eliminar usuario de Keycloak',
        message: `Se borrara el usuario <strong>${this.username || 'seleccionado'}</strong> de Keycloak. Esta accion no se puede deshacer.`,
        requiresReason: true
      },
      size: 'sm',
      layout: 'confirm',
      tone: 'danger',
    }));

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (!result?.confirmed || !this.userId) {
          return;
        }

        this.isDeleting = true;
        this.loadError = null;
        this.cdr.markForCheck();

        this.userService.deleteUser(this.userId, result.deletionReason)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => this.router.navigate(['/users']),
            error: (error) => {
              this.loadError = error?.error?.detail || error?.error?.message || 'No se pudo eliminar el usuario.';
              this.isDeleting = false;
              this.cdr.markForCheck();
            }
          });
      });
  }

  private initializeForm(): void {
    this.editorForm = this.fb.group({
      username: ['', Validators.required],
      email: ['', Validators.email]
    });
  }

  private setupPersonSearch(): void {
    this.personSearchControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap(() => {
          this.isSearchingPeople = true;
          this.cdr.markForCheck();
        }),
        switchMap(value => {
          const searchTerm = typeof value === 'string' ? value : this.displayPerson(value);
          if (!searchTerm || searchTerm.trim().length < 2) {
            return of([]);
          }

          return this.userService.getEligiblePeople(searchTerm.trim(), 1, 10).pipe(
            catchError(() => of({ items: [] }))
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((response: any) => {
        this.eligiblePeople = Array.isArray(response) ? response : response.items ?? [];
        this.isSearchingPeople = false;
        this.cdr.markForCheck();
      });
  }

  private loadPreselectedPerson(): void {
    const personId = this.activatedRoute.snapshot.queryParamMap.get('personId');
    if (!personId) {
      return;
    }

    this.isSearchingPeople = true;
    this.cdr.markForCheck();

    this.personService.getPerson(personId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: person => {
          const eligiblePerson = {
            id: person.id,
            firstName: person.firstName,
            lastName: person.lastName,
            email: person.email,
            documentNumber: person.documentNumber,
            membershipUnitId: person.communityId,
            membershipUnitName: person.communityName
          } as EligiblePerson;

          this.eligiblePeople = [eligiblePerson];
          this.personSearchControl.setValue(eligiblePerson, { emitEvent: false });
          this.onPersonSelected(eligiblePerson);
          this.isSearchingPeople = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loadError = 'No se pudo cargar la persona seleccionada.';
          this.isSearchingPeople = false;
          this.cdr.markForCheck();
        }
      });
  }

  private loadUserForDetail(id: string): void {
    this.isLoading = true;
    this.isSaving = false;
    this.loadError = null;
    this.editorForm.get('username')?.disable();
    this.cdr.markForCheck();

    this.userService.getUserById(id)
      .pipe(
        switchMap(user => {
          this.applyUserDetail(user);

          return this.hasLocalUser
            ? forkJoin({
                assignments: this.userService.getRoleAssignments(id).pipe(catchError(() => of([]))),
                activitySummary: this.userService.getUserActivitySummary(id).pipe(catchError(() => of(null)))
              })
            : of({ assignments: [], activitySummary: null });
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (result) => {
          this.roleAssignments = result.assignments;
          this.roleAssignmentsTableData = result.assignments;
          this.activitySummary = result.activitySummary;
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.loadError = error?.error?.detail || 'No se pudo cargar el usuario.';
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      });
  }

  private loadSelfProfileForDetail(): void {
    this.isLoading = true;
    this.isSaving = false;
    this.loadError = null;
    this.editorForm.get('username')?.disable();
    this.cdr.markForCheck();

    this.userService.getCurrentUserDetail()
      .pipe(
        switchMap(user => {
          this.applyUserDetail(user);
          this.userId = user.id ?? null;

          return this.hasLocalUser
            ? this.userService.getCurrentUserRoleAssignments().pipe(catchError(() => of([])))
            : of([]);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (assignments) => {
          this.roleAssignments = assignments;
          this.roleAssignmentsTableData = assignments;
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.loadError = error?.error?.detail || 'No se pudo cargar el perfil.';
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      });
  }

  private applyUserDetail(user: {
    username?: string | null;
    fullName?: string | null;
    email?: string | null;
    documentNumber?: string | null;
    membershipUnitName?: string | null;
    hasLocalUser?: boolean;
  }): void {
    this.username = user.username || '';
    this.breadcrumbService.setDynamicCrumb('id', this.username || 'Usuario');
    this.userFullName = user.fullName || '';
    this.userEmail = user.email || '';
    this.userDocument = user.documentNumber || '';
    this.membershipUnitName = user.membershipUnitName || '';
    this.hasLocalUser = user.hasLocalUser === true;
    this.editorForm.patchValue({
      username: user.username,
      email: user.email
    });
  }

  private populatePersonInfo(person: EligiblePerson): void {
    this.userFullName = `${person.firstName ?? ''} ${person.lastName ?? ''}`.trim();
    this.userEmail = person.email ?? '';
    this.userDocument = person.documentNumber ?? '';
    this.membershipUnitName = person.membershipUnitName ?? '';
  }

  private buildCreateRequest(): CreateUserRequest {
    const email = this.editorForm.get('email')?.value?.trim();

    return {
      memberId: this.selectedPerson!.id!,
      username: this.editorForm.get('username')?.value,
      ...(email ? { email } : {})
    };
  }

  private persistRoleAssignments(assignments: UserRoleAssignmentDto[]): void {
    if (!this.userId || this.isSaving || this.isSelfProfile) {
      return;
    }

    this.isSaving = true;
    this.loadError = null;
    this.cdr.markForCheck();

    this.userService.updateRoleAssignments(this.userId, this.buildRoleAssignmentsRequest(assignments))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.loadUserForDetail(this.userId!),
        error: (error) => {
          this.loadError = error?.error?.detail || error?.error?.message || 'No se pudo guardar el acceso.';
          this.isSaving = false;
          this.cdr.markForCheck();
        }
      });
  }

  private buildRoleAssignmentsRequest(assignments: UserRoleAssignmentDto[]): UpsertUserRoleAssignmentDto[] {
    return assignments.map(assignment => ({
      role: assignment.role,
      organizationUnitId: assignment.organizationUnitId,
      includeDescendants: true,
      validFrom: null,
      validTo: null,
    }) satisfies UpsertUserRoleAssignmentDto);
  }

  private validateAssignments(assignments: UserRoleAssignmentDto[]): string | null {
    const keys = assignments.map(assignment => `${assignment.role}:${assignment.organizationUnitId}`);
    if (this.hasDuplicates(keys)) {
      return 'No repitas la misma combinación de rol y unidad.';
    }

    const unitManagerUnitIds = assignments
      .filter(assignment => assignment.role === 'UnitManager')
      .map(assignment => assignment.organizationUnitId);
    const hasCoveredServant = assignments
      .some(assignment => assignment.role === 'Servant' && unitManagerUnitIds.includes(assignment.organizationUnitId));

    return hasCoveredServant
      ? 'Responsable de unidad ya incluye permisos de siervo en la unidad y sus descendientes.'
      : null;
  }

  private getRoleAssignmentSortValue(assignment: UserRoleAssignmentDto, sortBy: string): string {
    if (sortBy === 'unit') {
      return assignment.organizationUnitName ?? '';
    }

    return this.getRoleLabel(assignment.role);
  }

  private hasDuplicates(values: string[]): boolean {
    return new Set(values).size !== values.length;
  }

  private dialogResultToAssignment(result: UserRoleAssignmentDialogResult, id = 0): UserRoleAssignmentDto {
    return {
      id,
      role: result.request.role as UserRole,
      organizationUnitId: result.request.organizationUnitId,
      organizationUnitName: result.organizationUnitName,
      organizationUnitLevelId: result.organizationUnitLevelId,
      includeDescendants: true,
      validFrom: null,
      validTo: null,
    };
  }

  private suggestUsername(person: EligiblePerson): string {
    const base = (person.email?.split('@')[0] || `${person.firstName}.${person.lastName}`).toLowerCase();
    return base
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9._-]/g, '');
  }
}
